import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { type SerializedError } from '@reduxjs/toolkit';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { withDataHandling } from 'src/shared/HOC/withDataHandling';
import { type FlexflowEmployeeDataToDisplay } from '../../Contracts/types';
import { EmployeeDetailsTab } from './constants';
import { getTabId, getTabPanelId } from './helpers';
import AnnualConsumptionGroup from '../AnnualConsumptionGroup';
import { ConfigurationTab } from './components/ConfigurationTab';
import { GeneralTab } from './components/GeneralTab';
import { TabPanel } from './components/TabPanel';

type EmployeeDetailsContentProps = {
  isLoading: boolean;
  isError: boolean;
  error: FetchBaseQueryError | SerializedError | Error | undefined;
  data: FlexflowEmployeeDataToDisplay;
  isSuspendedBannerVisible: boolean;
  suspendedByLimit: boolean;
};

const EmployeeDetailsContent = ({ isSuspendedBannerVisible, suspendedByLimit }: EmployeeDetailsContentProps) => {
  const { t } = useTranslation(['flexflow']);
  const [tab, setTab] = React.useState<EmployeeDetailsTab>(EmployeeDetailsTab.GENERAL);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: EmployeeDetailsTab) => {
    setTab(newValue);
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={handleTabChange} aria-label={t('employee details')}>
          <Tab
            label={t('employeeDetails.tabs.general')}
            value={EmployeeDetailsTab.GENERAL}
            id={getTabId(EmployeeDetailsTab.GENERAL)}
            aria-controls={getTabPanelId(EmployeeDetailsTab.GENERAL)}
          />
          <Tab
            label={t('employeeDetails.tabs.configuration')}
            value={EmployeeDetailsTab.CONFIGURATION}
            id={getTabId(EmployeeDetailsTab.CONFIGURATION)}
            aria-controls={getTabPanelId(EmployeeDetailsTab.CONFIGURATION)}
          />
          <Tab
            label={t('employeeDetails.tabs.annualConsumption')}
            value={EmployeeDetailsTab.ANNUAL_CONSUMPTION}
            id={getTabId(EmployeeDetailsTab.ANNUAL_CONSUMPTION)}
            aria-controls={getTabPanelId(EmployeeDetailsTab.ANNUAL_CONSUMPTION)}
          />
        </Tabs>
      </Box>

      <TabPanel value={EmployeeDetailsTab.GENERAL} currentTab={tab}>
        <GeneralTab isSuspendedBannerVisible={isSuspendedBannerVisible} suspendedByLimit={suspendedByLimit} />
      </TabPanel>

      <TabPanel value={EmployeeDetailsTab.CONFIGURATION} currentTab={tab}>
        <ConfigurationTab />
      </TabPanel>

      <TabPanel value={EmployeeDetailsTab.ANNUAL_CONSUMPTION} currentTab={tab}>
        <AnnualConsumptionGroup />
      </TabPanel>
    </Box>
  );
};

export const WrappedComponent = withDataHandling(EmployeeDetailsContent);
